import { validationAccount } from "api/interna-api";
import Button from "components/atoms/Button";
import Fielderror from "components/atoms/Fielderror";
import Icon from "components/atoms/Icon";
import Link from "components/atoms/Link";
import LoadingState from "components/atoms/LoadingState";
import TextareaField from "components/atoms/TextareaField";
import {
  FieldcellExtended,
  FieldCellRightText,
  FieldRowExtended,
  flexCss,
  FormInputUserUnLogging,
  FormInputUserUnLoggingInputWrapper,
  FormInputUserUnLoggingTextfield,
  LoggedUserInfo,
  LoggedUserInfoBar,
  LogoutTextLink,
  OtherAction,
  SubmitBlock,
} from "components/pages/ProductDetailPage/styled";
import colors, { alphaColor } from "constants/colors";
import { KEY_CAPTCHA } from "constants/env"
import { maxQuery, minQuery, SizeMax, SizeMin } from "constants/media";
import { VALIDATION_PATTERN } from "constants/validation";
import { useSelector } from "hooks/useApp";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestPostRating, resetRating } from "redux/actions/general/rating";
import styled, { css } from "styled-components/macro";
import { joinClassnames } from "utils/map-modifier";
import { getObject, isVideo, notEmptyObject } from "utils/utils-common";
import Fieldrow, { Fieldcell } from "../Fieldrow";

interface ReviewProductFormProps {
  productId: number;
  onSubmitReviewProductForm: (
    result: boolean,
    errors?: {
      field: string;
      message: string;
    }[],
  ) => void;
  onLogout: () => void;
  onloading?: boolean;
}

type FormData = {
  customer_name?: string;
  customer_phone: string;
  customer_email: string;
  numbers_rating: number;
  comment: string;
  criterias: {
    criterias: { criteria_name: string; criteria_id: number; value: number }[];
  };
};

const ReviewProductForm: React.FC<ReviewProductFormProps> = ({
  productId,
  onSubmitReviewProductForm,
  onLogout,
  onloading,
}: ReviewProductFormProps) => {
  const { userInfor: userInformation } = useSelector(state => state);
  const { rating } = useSelector(state => state.general);
  const { criterias } = useSelector(state => state.products);

  const {
    handleSubmit,
    setValue,
    getValues,
    formState,
    errors,
    register,
  } = useForm<FormData>({
    mode: "onBlur",
    defaultValues: {
      comment: "",
      product_id: productId,
      numbers_rating: 5,
    },
  });

  const [files, setFiles] = useState([]);

  const RULE_UPLOADS = {
    accept: "image/*, video/*",
    maxFiles: 5,
    maxSize: 10485760,
  };

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    accept: RULE_UPLOADS.accept,
    maxFiles: RULE_UPLOADS.maxFiles,
    maxSize: RULE_UPLOADS.maxSize,
    onDrop: acceptedFiles => {
      setFiles(
        acceptedFiles.map(file =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );
    },
  });

  const thumb = files.map((file, index) => (
    <RealUploadedImage key={file.path}>
      <TrashRealImageBtn
        iconName="trash"
        onClick={() => removeUploadedImage(file)}
      />
      {isVideo(file.name) ? (
        <video controls>
          <source src={file.preview} type={file.type} />
          Your browser does not support HTML video.
        </video>
      ) : (
        <img
          src={file.preview}
          alt={file.path || `Review Product Form ${index}`}
        />
      )}
    </RealUploadedImage>
  ));

  useEffect(() => {
    files.forEach(file => URL.revokeObjectURL(file.preview));
  }, [files]);

  useEffect(() => {
    return () => {
      dispatch(resetRating());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMaxFileError = (message: string) => message === "Too many files";

  const isMaxSizeError = (message: string) =>
    message.includes("File is larger than");

  const fileRejectionItems = fileRejections.map(({ file, errors }) => {
    return (
      <ErrorItem key={file.path}>
        {file.path} - {(file.size / 1024 / 1024).toFixed(2)} Mbs
        <ul>
          {errors.map(e => (
            <li key={e.code}>
              {isMaxSizeError(e.message.toString()) ? (
                <del>
                  Kích thước file lớn hơn quy định - tối đa
                  {(RULE_UPLOADS.maxSize / 1024 / 1024).toFixed(2)}Mbs
                </del>
              ) : isMaxFileError(e.message.toString()) ? (
                <span>Quá {RULE_UPLOADS.maxFiles} file quy định.</span>
              ) : (
                <div>{e.message}</div>
              )}
            </li>
          ))}
        </ul>
      </ErrorItem>
    );
  });

  const [isLoading, setIsLoading] = useState(false);
  const touched = Object.values(formState.touched);

  const [criteriasState, setCriteriasState] = useState<CriteriaType[]>([]);

  const handleChangeCriterias = (e: any) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    setCriteriasState(
      criteriasState.map(criteria => {
        if (criteria.criteria_name === name) {
          criteria.value = Boolean(Number(value));
        }
        return criteria;
      }),
    );
  };

  useEffect(() => {
    if (Object.keys(criterias.data).length) {
      const items = Object.keys(criterias.data).map(key => ({
        criteria_id: criterias.data[key].id,
        criteria_name: criterias.data[key].name,
        value: -1,
      }));
      if (items.length) {
        setCriteriasState(items);
      }
    }
  }, [criterias]);

  const dispatch = useDispatch();

  const onSubmit = (data: any) => {
    setIsLoading(true);
    const {
      comment,
      customer_name,
      customer_email,
      customer_phone,
      numbers_rating,
    } = data;

    if (!customer_name || !customer_email) {
      setIsLoading(false);
      toast.error("Đã xảy ra lỗi! Tên & Email không tồn tại!");
    } else if (selectedRate === -1) {
      setIsLoading(false);
      setShowErrorRating(true);
      toast.error("Vui lòng chọn rating");
    } else {
      // Validation account:
      validationAccount({
        productId,
        email: customer_email,
        phone: customer_phone || "123456789",
      })
        .then(data => {
          if (data.accept) {
            // If pass validation => approve for submit rating data:
            const ratingData = new FormData();

            criteriasState.forEach(criteria => {
              criteria.value = Number(criteria.value);
            });

            ratingData.append("product_id", productId);
            ratingData.append("comment", comment);
            ratingData.append("customer_name", customer_name);
            ratingData.append("customer_phone", customer_phone);
            ratingData.append("customer_email", customer_email);
            ratingData.append("numbers_rating", numbers_rating);

            if (criteriasState) {
              ratingData.append(
                "criterias",
                JSON.stringify({
                  criterias: criteriasState.filter(item => item.value !== -1),
                }),
              );
            }

            if (files) {
              for (let i = 0; i < files.length; i++) {
                const item = files[i];
                if (item) {
                  ratingData.append(`files[${i}]`, item);
                }
              }
            }

            dispatch(requestPostRating(ratingData));
          } else {
            if ("number_of_comments_remaining" in data) {
              // ${data["number_of_comments_remaining"]}
              toast.error(
                `Đánh giá không thành công! Bạn không có lượt đánh giá.`,
              );
            } else {
              toast.error("Đánh giá không thành công!");
            }
          }
        })
        .catch(error => {
          // toast.error(error.map((msg: any) => msg.message || "Unknown error"));
          console.error(error);
        })
        .finally(() => {
          setIsLoading(false);
          if (showErrorRating) {
            setShowErrorRating(false);
          }
        });
    }
  };

  const [selectedRate, setSelectedRate] = useState(-1);
  const [showErrorRating, setShowErrorRating] = useState(false);

  useEffect(() => {
    if (getObject(rating.data, "keys").length) {
      const { error, loading } = rating;
      if (!error) {
        toast.info(
          "Thông tin đánh giá đã được gửi thành công. Cảm ơn bạn đã hoàn tất đánh giá.",
        );
        onSubmitReviewProductForm(true);
      } else {
        toast.error(error.map((item: ErrorField) => item.message).join(","));
        onSubmitReviewProductForm(false);
      }
      setIsLoading(loading);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating]);

  useEffect(() => {
    register(
      {
        name: "numbers_rating",
      },
      { required: true },
    );

    if (userInformation) {
      register({
        name: "customer_name",
        value: userInformation.name,
      });
      register({
        name: "customer_phone",
        value: userInformation.phone,
      });
      register({
        name: "customer_email",
        value: userInformation.email,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register]);

  const removeUploadedImage = removeFile => {
    if (files) {
      setFiles(files.filter(file => file !== removeFile) || []);
    }
  };

  const [comment, setComment] = useState("");

  const handleChange = (e: any) => {
    const { value } = e.currentTarget;
    setComment(value);
  };

  const [needVerify, setNeedVerify] = useState(false);
  const [count, setCount] = useState<number>();

  useEffect(() => {
    if (notEmptyObject(errors)) {
      const countSave = Cookies.get("needVerify.ReviewProductForm");
      setCount(countSave ? Number(countSave) + 1 : count ? count + 1 : 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    if (count !== undefined) {
      setNeedVerify(count === 3);
      Cookies.set(
        "needVerify.ReviewProductForm",
        count ? count.toString() : "1",
        {
          expires: 1, // 1 day
        },
      );
    }
  }, [count]);

  useEffect(() => {
    const checkNeedVerifyCount = Cookies.get("needVerify.ReviewProductForm");
    if (checkNeedVerifyCount) {
      setCount(Number(checkNeedVerifyCount));
      if (Number(checkNeedVerifyCount) >= 3) {
        setNeedVerify(true);
      }
    }
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={"input-review-form"}>
        <InputYourCommentBlock>
          <Fieldrow modifiers={["input"]}>
            <Fieldcell>
              <FieldRowExtended modifiers={["input"]}>
                <FieldCellRightText>
                  <TextareaField
                    value={comment}
                    name="comment"
                    placeholder="Chia Sẻ Cảm Nhận"
                    modifiers={
                      (touched && formState.touched.comment) || errors.comment
                        ? errors.comment
                          ? ["invalid"]
                          : ["valid"]
                        : []
                    }
                    refs={register({
                      required: {
                        value: true,
                        message: "Bạn chưa nhập nội dung",
                      },
                      minLength: {
                        value: 15,
                        message: "Tối thiểu 15 ký tự",
                      },
                    })}
                    onChange={handleChange}
                  />
                  {errors.comment && (
                    <Fielderror>{errors.comment.message}</Fielderror>
                  )}
                  <span className="count-comment-character">
                    {(getValues().comment && getValues().comment.length) || 0}{" "}
                    ký tự (tối thiểu 15)
                  </span>
                </FieldCellRightText>
              </FieldRowExtended>
            </Fieldcell>
          </Fieldrow>
        </InputYourCommentBlock>

        <CustomerUploadedPicterBlock>
          <h3>Gửi hình chụp thực tế (tối đa 5)</h3>
          <CustomerUploadedPicterList>
            {thumb}
            <LabelFileUpload {...getRootProps({ className: "dropzone" })}>
              <Icon iconName="plus" />
              <input {...getInputProps()} />
            </LabelFileUpload>
          </CustomerUploadedPicterList>
          {Object.keys(fileRejectionItems).length > 0 && (
            <ErrorList>{fileRejectionItems}</ErrorList>
          )}
        </CustomerUploadedPicterBlock>

        <CustomerFeelingReview onError={showErrorRating}>
          <h3>Bạn cảm thấy sản phẩm như thế nào? (Chọn sao tương ứng)</h3>
          <CustomerFeelingAnswer>
            {[
              { value: 1, name: "Rất tệ" },
              { value: 2, name: "Tệ" },
              { value: 3, name: "Bình thường" },
              { value: 4, name: "Tốt" },
              { value: 5, name: "Rất Tốt" },
            ].map(feeling => (
              <span
                key={feeling.value}
                onClick={() => {
                  setSelectedRate(feeling.value);
                  setValue("numbers_rating", feeling.value);
                }}
                className={joinClassnames(
                  selectedRate === feeling.value ? "selected" : "",
                  feeling.value <= selectedRate ? "prev-selected" : "",
                )}
              >
                <span>{feeling.name}</span>
                <Icon
                  iconName={
                    selectedRate === feeling.value ||
                      feeling.value <= selectedRate
                      ? "star-orange"
                      : "star-light-gray"
                  }
                />
              </span>
            ))}
          </CustomerFeelingAnswer>
        </CustomerFeelingReview>

        {onloading ? (
          <LoadingState />
        ) : (
          <QuestionsForClients>
            {criteriasState &&
              criteriasState.map(criteria => (
                <QuestionForClient key={criteria.criteria_id}>
                  <span>{criteria.criteria_name}</span>
                  <CheckboxButtonBox>
                    <CheckBoxButton value={1}>
                      <input
                        type="radio"
                        defaultChecked={false}
                        value={1}
                        name={criteria.criteria_name}
                        onChange={e => handleChangeCriterias(e)}
                      />
                      <span>Có</span>
                    </CheckBoxButton>
                    <CheckBoxButton value={0}>
                      <input
                        type="radio"
                        defaultChecked={false}
                        value={0}
                        name={criteria.criteria_name}
                        onChange={e => handleChangeCriterias(e)}
                      />
                      <span>Không</span>
                    </CheckBoxButton>
                  </CheckboxButtonBox>
                </QuestionForClient>
              ))}
          </QuestionsForClients>
        )}

        <UserInformationReviewBlock>
          {userInformation ? (
            <LoggedUserInfoBar>
              <LoggedUserInfo>
                <Icon iconName="user-review-icon" />
                <span>{userInformation.name || "unknown"}</span>
              </LoggedUserInfo>
              <OtherAction>
                <span>Không phải tài khoản này?</span>
                <Link onClick={onLogout}>
                  <LogoutTextLink className="d-flex align-items-center">
                    Đăng xuất
                  </LogoutTextLink>
                </Link>
              </OtherAction>
            </LoggedUserInfoBar>
          ) : (
            <FormInputUserUnLogging>
              <FieldRowExtended>
                <FieldcellExtended>
                  <FormInputUserUnLoggingInputWrapper>
                    <FormInputUserUnLoggingTextfield
                      name="customer_name"
                      placeholder="Họ & Tên*"
                      value={getValues().customer_name}
                      modifiers={
                        (touched && formState.touched.customer_name) ||
                          errors.customer_name
                          ? errors.customer_name
                            ? ["invalid"]
                            : ["valid"]
                          : []
                      }
                      refs={register({
                        required: {
                          value: true,
                          message: "Bạn chưa nhập - Họ và tên",
                        },
                      })}
                    />
                    {errors.customer_name && (
                      <Fielderror>{errors.customer_name.message}</Fielderror>
                    )}
                  </FormInputUserUnLoggingInputWrapper>
                </FieldcellExtended>
                <FieldcellExtended>
                  <FormInputUserUnLoggingInputWrapper>
                    <FormInputUserUnLoggingTextfield
                      name="customer_phone"
                      placeholder="Số Điện Thoại*"
                      value={getValues().customer_phone}
                      modifiers={
                        (touched && formState.touched.customer_phone) ||
                          errors.customer_phone
                          ? errors.customer_phone
                            ? ["invalid"]
                            : ["valid"]
                          : []
                      }
                      refs={register({
                        required: {
                          value: true,
                          message: "Bạn chưa nhập - Sđt",
                        },
                        pattern: {
                          value: /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s.]{0,1}[0-9]{3}[-\s.]{0,1}[0-9]{4}$/,
                          message: "SĐT không đúng định dạng",
                        },
                      })}
                    />
                    {errors.customer_phone && (
                      <Fielderror>{errors.customer_phone.message}</Fielderror>
                    )}
                  </FormInputUserUnLoggingInputWrapper>
                </FieldcellExtended>
                <FieldcellExtended>
                  <FormInputUserUnLoggingInputWrapper>
                    <FormInputUserUnLoggingTextfield
                      name="customer_email"
                      placeholder="Email*"
                      value={getValues().customer_email}
                      modifiers={
                        (touched && formState.touched.customer_email) ||
                          errors.customer_email
                          ? errors.customer_email
                            ? ["invalid"]
                            : ["valid"]
                          : []
                      }
                      refs={register({
                        required: {
                          value: true,
                          message: "Bạn chưa nhập - email",
                        },
                        pattern: {
                          value: VALIDATION_PATTERN.common.email,
                          message: "Email không đúng định dạng",
                        },
                      })}
                    />
                    {errors.customer_email && (
                      <Fielderror>{errors.customer_email.message}</Fielderror>
                    )}
                  </FormInputUserUnLoggingInputWrapper>
                </FieldcellExtended>
              </FieldRowExtended>
            </FormInputUserUnLogging>
          )}
        </UserInformationReviewBlock>

        <SubmitBlock>
          {needVerify && KEY_CAPTCHA ? (
            <div className="form-recapcha">
              <form>
                <ReCAPTCHA
                  sitekey={KEY_CAPTCHA}
                  onChange={(value: any) => {
                    if (value) {
                      Cookies.remove("needVerify.ReviewProductForm");
                      setNeedVerify(false);
                      setCount(undefined);
                    }
                  }}
                />
              </form>
            </div>
          ) : (
            <Button type="submit">GỬI ĐÁNH GIÁ NGAY</Button>
          )}
        </SubmitBlock>
      </form>
      {isLoading && <LoadingState />}
    </>
  );
};

const InputYourCommentBlock = styled.div`
  margin-bottom: 1em;
  margin-top: 1em;
  padding: 0 1.5em;
`;

const TrashRealImageBtn = styled(Icon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  z-index: 2;
  transition: 0.45s;
`;

const RealUploadedImage = styled.span`
  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 72px;
  cursor: pointer;
  overflow: hidden;

  &:before {
    content: "";
    display: block;
    width: 100%;
    background-color: ${alphaColor.black025};
    z-index: 1;
    padding-bottom: 100%;
  }

  img,
  video {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: 0.45s;
    max-width: 100%;
  }

  &:hover {
    ${TrashRealImageBtn} {
      transform: translate(-50%, -50%) scale(1);
    }
    img,
    video {
      transform: translate(-50%, -50%) scale(1.15);
    }
  }
`;

const QuestionsForClients = styled.div`
  background-color: ${colors.snow};
  padding: 1.25em 1.5em;
`;

const UserInformationReviewBlock = styled.div`
  background-color: ${colors.whiteSmoke};
  padding: 1.25em 1.5em;

  ${LoggedUserInfoBar}, ${FormInputUserUnLogging} {
    margin-bottom: 0;
  }
`;

const QuestionForClient = styled.div`
  ${flexCss};
  justify-content: space-between;
  color: ${colors.dimGray};

  ${maxQuery(
  SizeMax.Small,
  css`
      flex-direction: column;
      align-items: flex-start;
    `,
)}
`;

const LabelFileUpload = styled.div`
  ${flexCss};
  justify-content: center;
  width: 72px;
  height: 72px;
  border: 1px dashed gray;
  cursor: pointer;

  > input {
    display: none;
  }

  &:hover {
    opacity: 0.75;
  }
`;

const CustomerUploadedPicterList = styled.div`
  ${flexCss};
  margin-top: 7px;

  ${RealUploadedImage} {
    margin-left: 0.25em;

    &:first-of-type {
      margin-left: 0;
    }
  }

  ${LabelFileUpload} {
    margin-left: 0.25em;
  }
`;

const CustomerUploadedPicterBlock = styled.div`
  padding: 0 1.5em;

  > h3 {
    font-size: 16px;
    font-weight: normal;
    color: ${colors.dimGray};
    margin-bottom: 0;
  }
`;

type CheckBoxButtonType = {
  value: number;
};
const CheckBoxButton = styled.label<CheckBoxButtonType>`
  display: inline-block;
  position: relative;
  cursor: pointer;

  ${maxQuery(
  SizeMax.Small,
  css`
      &:not(:first-of-type) {
        margin-left: 0.5em;
      }
    `,
)}

  > input {
    display: none;

    & + span {
      ${flexCss};
      justify-content: center;
      border-radius: 3px;
      transition: 0.25s;
      background-color: white;
      border: 2px solid
        ${({ value }) => (value ? colors.richElectricBlue : colors.lava)};
      color: ${({ value }) => (value ? colors.richElectricBlue : colors.lava)};
      font-weight: bold;
      font-size: 12px;
      text-transform: uppercase;
      padding: 0.25em 0.5em;

      width: 108px;
      height: 36px;
      ${minQuery(SizeMin.Medium, css``)}
    }

    &:checked {
      & + span {
        color: white;
        background-color: ${({ value }) =>
    value ? colors.richElectricBlue : colors.lava};
      }
    }
  }

  &:hover {
    opacity: 0.75;
  }

  &:last-of-type {
    margin-left: 24px;
  }
`;

const CheckboxButtonBox = styled.div`
  flex-direction: row;
  align-items: flex-start;
  ${maxQuery(
  SizeMax.Small,
  css`
      margin-top: 0.5em;
    `,
)}
`;

const CustomerFeelingReview = styled.div<{ onError?: boolean }>`
  ${flexCss};
  justify-content: space-between;
  padding: 1.25em 1.5em;

  ${maxQuery(
  SizeMax.Small,
  css`
      flex-direction: column;
      align-items: flex-start;
      margin-top: 0.5em;
    `,
)}

  > h3 {
    font-size: 16px;
    font-weight: normal;
    color: ${colors.dimGray};
    margin-bottom: 0;
  }

  span {
    color: ${colors.ashGrey};
  }

  ${({ onError }) =>
    onError &&
    css`
      animation: OnErrorShow 0.45s;
      animation-iteration-count: 5;
    `}

  @keyframes OnErrorShow {
    from {
      background-color: white;
    }
    to {
      background-color: ${alphaColor.electricCrimson045};
    }
  }
`;

const CustomerFeelingAnswer = styled.div`
  width: 100%;
  
  ${maxQuery(
  SizeMax.Small,
  css`
      margin-top: 0.5em;
    `,
)}

  ${minQuery(
  SizeMin.Small,
  css`
      width: 370px;
    `,
)}
  ${flexCss};
  justify-content: space-between;
  cursor: pointer;

  > span {
    flex: 1;
    ${flexCss};
    flex-direction: column-reverse;
    justify-content: center;

    > span {
      font-size: 12px;
      white-space: nowrap;
      margin-bottom: 8px;
    }

    &.selected {
      > span {
        color: ${colors.tangerine};
      }
    }

    &:hover {
      opacity: 0.75;
    }
  }
`;

const ErrorItem = styled.li`
  font-size: 12px;
  color: ${colors.coralRed};
`;

const ErrorList = styled.ul`
  padding-left: 1em;
  margin-top: 7px;
`;

export default ReviewProductForm;
